import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

import { Alert, Button, Stack } from 'react-bootstrap';
import { MdFilterAlt, MdFilterAltOff } from 'react-icons/md';

import api from 'api';
import { getStateLabel, getStatusLabel } from 'helpers/forms';
import { ProgramTypeAndSubTypeLabel } from 'utils/enums';
import useRequest from 'hooks/useRequest';
import { formatUTCDate } from 'utils/dateTime';

import Table from 'components/elements/Table';
import SiteVisitCalendar from 'components/forms/SiteVisitCalendar';
import ListToolbar from 'components/layout/ListToolbar';
import SectionWrapper from 'components/layout/SectionWrapper';
import AdminVerificationMenu from 'components/templates/menus/AdminVerificationMenu';
import { FlexColumn } from 'resources/styles/display';

import BurnCenterFilter from 'components/elements/BurnCenterFilter';
import ExtraFilters from 'pages/verification/admin/filters/ExtraFilters';

import {
  createInitialVerificationListFilters,
  useVerificationListFilter,
} from './context/VerificationListFilterContext';
import ExportListButton from './ExportListButton';

const initFilters = createInitialVerificationListFilters();

const columns = [
  {
    id: 'bcqpBurnCenterName',
    label: 'Site Name',
    headerStyle: { width: '40%' },
  },
  {
    id: 'applicationType',
    label: 'Program Type',
    // eslint-disable-next-line max-len
    override: ({ applicationType }) => ProgramTypeAndSubTypeLabel[applicationType] || applicationType,
  },
  {
    id: 'state',
    label: 'Review Status',
    override: ({ state }) => getStateLabel(state),
  },
  {
    id: 'siteVisitAt',
    label: 'Site Visit Date',
    override: ({ siteVisitAt }) => formatUTCDate(siteVisitAt),
  },
  {
    id: 'status',
    label: 'Final Determination',
    override: ({ status }) => getStatusLabel(status),
  },
  {
    id: 'expiredAt',
    label: 'Expiration Date',
    override: ({ expiredAt }) => formatUTCDate(expiredAt),
  },
];

const VerificationList = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const {
    isLoading,
    error,
    doRequest: getListData,
    data: listData,
  } = useRequest(api.getAdminOverview);

  const {
    page,
    handlePageChange,
    sortBy,
    sortOrder,
    handleSortChange,
    filters,
    handleFilterChange,
    handleFiltersReplace,
    showExtraFilters,
    toggleExtraFilters,
  } = useVerificationListFilter();

  const requestParams = React.useMemo(
    () => ({
      order: sortBy,
      orderDirection: sortOrder,
      ...filters,
    }),
    [sortBy, sortOrder, filters],
  );

  const fetchListData = React.useCallback(
    () => getListData({
      ...requestParams,
      Page: page,
    }),
    [getListData, page, requestParams],
  );

  React.useEffect(() => {
    fetchListData();
  }, [fetchListData]);

  return (
    <SectionWrapper title="Verification Administrator Dashboard">
      <Stack
        direction="horizontal"
        gap={3}
        className="align-items-start"
        style={{ minWidth: 0 }}
      >
        <FlexColumn style={{ gap: '1rem', flexShrink: 0 }}>
          <AdminVerificationMenu />
          <SiteVisitCalendar />
        </FlexColumn>
        <Stack gap={3} style={{ flex: 1, minWidth: 0 }}>
          <ListToolbar
            left={(
              <Button to="new" as={Link} variant="secondary">
                New Verification
              </Button>
            )}
            right={(
              <Stack
                direction="horizontal"
                gap={2}
                className="flex-wrap justify-content-end"
                style={{ minWidth: 0 }}
              >
                <div style={{ width: '100%', maxWidth: '400px', minWidth: 0 }}>
                  <BurnCenterFilter
                    value={filters.bcqpBurnCenterId}
                    onChange={(value) => handleFilterChange('bcqpBurnCenterId', value)}
                  />
                </div>
                <Button
                  variant="secondary"
                  onClick={toggleExtraFilters}
                  className="flex-shrink-0"
                >
                  {showExtraFilters ? <MdFilterAltOff /> : <MdFilterAlt />}
                </Button>
                <div className="flex-shrink-0">
                  <ExportListButton requestParams={requestParams} />
                </div>
              </Stack>
            )}
          />
          {showExtraFilters && (
            <ExtraFilters
              filters={filters}
              onFilter={handleFilterChange}
              onClearFilter={() => handleFiltersReplace(initFilters)}
            />
          )}
          {error ? (
            <Alert variant="danger">Error Loading List</Alert>
          ) : (
            <Table
              columns={columns}
              items={listData?.items || []}
              minTableWidth="760px"
              handlePageChange={handlePageChange}
              handleOrderChange={handleSortChange}
              order={sortBy}
              orderDirection={sortOrder}
              pagination={{
                total: listData?.total || 0,
                limit: listData?.limit || 10,
                page,
              }}
              onClickRow={({ id }) => navigate(`${pathname}/${id}`)}
              isLoading={isLoading}
            />
          )}
        </Stack>
      </Stack>
    </SectionWrapper>
  );
};

export default VerificationList;
